import { useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  useSubmitHackathonProject,
  useHackathonProjectSubmissions,
} from '@/lib/hooks/useHackathons';

interface Props {
  batchId: string;
  hackathonId: string;
  componentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasTeam: boolean;
}

const labelClass = 'mb-1 block text-xs font-medium text-muted-foreground';

export function ProjectSubmissionDialog({
  batchId,
  hackathonId,
  componentId,
  open,
  onOpenChange,
  hasTeam,
}: Props) {
  const [repoUrl, setRepoUrl] = useState('');
  const [demoUrl, setDemoUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | undefined>();
  // Expanded by default so a returning team sees their previous submissions
  // without an extra click; the toggle just lets them collapse a long list.
  const [showHistory, setShowHistory] = useState(true);

  const { mutateAsync, isPending } = useSubmitHackathonProject(batchId, hackathonId, componentId);
  const { data: history } = useHackathonProjectSubmissions(batchId, hackathonId, componentId);

  function reset() {
    setRepoUrl('');
    setDemoUrl('');
    setVideoUrl('');
    setDescription('');
    setFile(undefined);
  }

  async function handleSubmit() {
    if (!repoUrl && !demoUrl && !videoUrl && !description && !file) {
      toast.error('Provide at least a link, description, or file.');
      return;
    }
    try {
      await mutateAsync({
        repo_url: repoUrl || undefined,
        demo_url: demoUrl || undefined,
        video_url: videoUrl || undefined,
        description: description || undefined,
        file,
      });
      toast.success('Project submitted.');
      onOpenChange(false);
      reset();
    } catch {
      toast.error('Failed to submit project.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Submit Project</DialogTitle>
        </DialogHeader>

        {!hasTeam ? (
          <p className="text-sm text-muted-foreground">
            You haven&apos;t been assigned to a team yet.
          </p>
        ) : (
          <div className="grid gap-3">
            <div>
              <label className={labelClass} htmlFor="ps-repo">Repo URL</label>
              <Input
                id="ps-repo"
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                placeholder="https://github.com/…"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="ps-demo">Live demo URL</label>
              <Input
                id="ps-demo"
                value={demoUrl}
                onChange={(e) => setDemoUrl(e.target.value)}
                placeholder="https://…"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="ps-video">Video demo URL</label>
              <Input
                id="ps-video"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://…"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="ps-description">Description</label>
              <textarea
                id="ps-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="What did you build, tech stack, known issues…"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="ps-file">Attach a file (optional)</label>
              <input
                id="ps-file"
                type="file"
                onChange={(e) => setFile(e.target.files?.[0])}
                className="w-full text-sm"
              />
            </div>

            {!!history?.length && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowHistory((v) => !v)}
                  aria-expanded={showHistory}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  {showHistory ? 'Hide' : 'Show'} submission history ({history.length})
                </button>
                {showHistory && (
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                    {history.map((s) => (
                      <li key={s.id} className="border-t border-border py-1 first:border-t-0">
                        {new Date(s.submitted_at).toLocaleString()} —{' '}
                        {s.repo_url || s.demo_url || s.video_url || s.description || 'file only'}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {hasTeam && (
            <Button onClick={handleSubmit} disabled={isPending}>Submit</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
